import React from "react";

/**
 * PaperGradientBG
 *
 * Ruhiger, kontrollierter Gradient-Background für Sections & Seiten.
 * Keine Effekte, keine Magie – nur klare Flächen.
 *
 * @param {React.ReactNode} children
 * @param {string} className
 * @param {string|object} variant
 * @param {number} gradientAngle
 * @param {boolean} animated
 * @param {boolean} fullHeight
 * @param {boolean} asBackground
 */
const PaperGradientBG = ({
  children,
  className = "",
  variant = "orange-blue-dark",
  gradientAngle = 0,
  animated = false,
  fullHeight = false,
  asBackground = false,
}) => {
  /**
   * Definierte, kontrollierte Gradients
   * – keine Sonderfälle
   * – gleiche Struktur für alle Varianten
   */
  const gradientDefinitions = {
    "fuchsia-violet": {
      className: "bg-gradient-to-br from-fuchsia-600 via-violet-700 to-slate-900",
      stops: ["#D946EF", "#7C3AED", "#0F172A"],
    },

    "orange-pink": {
      className: "bg-gradient-to-br from-orange-500 via-pink-500 to-orange-200",
      stops: ["#FB923C", "#EC4899", "#FB923C"],
    },

    "orange-blue-dark": {
      className: "bg-gradient-to-br from-slate-900 to-slate-800",
      stops: ["#0F172A", "#1E293B"],
    },

    "blue-purple": {
      className: "bg-gradient-to-br from-slate-900 via-purple-700 to-slate-900",
      stops: ["#0F172A", "#6D28D9", "#0F172A"],
    },

    "zenBrand": {
      className: "bg-gradient-to-br from-[#AC8E66] via-[#AC8E66] to-[#8B7355]",
      stops: ["#AC8E66", "#AC8E66", "#8B7355"],
    },

    /**
     * Zen Black – bewusst ruhig, bewusst neutral
     * kein Tailwind-Gradient, volle Kontrolle
     */
    "zenBlack": {
      className: "",
      stops: ["#0B0B0B", "#151515"],
      angle: 180,
    },
  };

  /**
   * Auswahl der Definition
   */
  const selectedDefinition =
    typeof variant === "string"
      ? gradientDefinitions[variant] || gradientDefinitions["zenBrand"]
      : gradientDefinitions["zenBrand"];

  /**
   * Effektiver Winkel
   */
  const effectiveAngle =
    typeof variant === "object"
      ? variant.angle ?? gradientAngle
      : selectedDefinition.angle ?? gradientAngle;

  /**
   * Effektive Stops
   */
  const effectiveStops =
    typeof variant === "object"
      ? variant.stops
      : selectedDefinition.stops;

  /**
   * Custom Style (nur wenn Stops existieren)
   */
  const customStyle =
    effectiveStops
      ? {
          backgroundImage: `linear-gradient(${effectiveAngle}deg, ${effectiveStops.join(", ")})`,
        }
      : {};

  return (
    <div
      className={`
        w-full
        ${asBackground ? "absolute inset-0 pointer-events-none" : ""}
        ${fullHeight ? "min-h-screen" : ""}
        ${selectedDefinition.className ?? ""}
        ${className}
      `}
      style={customStyle}
    >
      {children}
    </div>
  );
};

export default PaperGradientBG;
